/**
 * Amplitude 분석 — 앱 전체가 쓰는 유일한 진입점.
 *
 * 컴포넌트는 SDK 를 직접 부르지 말고 `track()` 만 쓴다.
 *  - `VITE_AMPLITUDE_API_KEY` 가 없으면 SDK 를 **불러오지도 초기화하지도 않는다.**
 *    `track()` 은 아무 일도 하지 않으므로 로컬·미리보기에서 에러가 나지 않는다.
 *  - SDK 는 동적 import 로 따로 받는다 → 키가 없는 빌드에서는 방문자 번들에 영향이 없고,
 *    키가 있어도 첫 화면 렌더를 막지 않는다. 그 사이에 발생한 이벤트는 큐에 쌓였다가 전송된다.
 *  - `/admin` 에서 시작한 탭은 추적하지 않는다.
 *
 * 디버그: `VITE_AMPLITUDE_DEBUG=true` 로 빌드하거나, 아무 페이지에나 `?amp_debug=1` 을 붙여 열면
 * 이벤트가 콘솔에 `[analytics]` 로 찍힌다(이 탭 세션 동안 유지). 키가 없을 때도 찍히므로
 * 키 없이 이벤트 이름·속성을 확인할 수 있다 — 이때 실제 전송은 하지 않는다.
 *
 * ⚠️ 개인정보: 폼 입력값·이메일·이름 등은 **절대 넣지 않는다.**
 * SDK 의 요소 클릭·폼 자동 수집(autocapture elementInteractions / formInteractions)도 꺼 두었다.
 */
import type { Types } from '@amplitude/analytics-browser'
import { captureTraffic, type Touch } from './traffic'

type BrowserClient = Types.BrowserClient
type Props = Record<string, string | number | boolean | null | undefined>

const API_KEY = (import.meta.env.VITE_AMPLITUDE_API_KEY ?? '').trim()
const SERVER_ZONE = import.meta.env.VITE_AMPLITUDE_SERVER_ZONE === 'EU' ? 'EU' : 'US'
/** 광고 차단기 회피용 자체 프록시 주소 (선택). 비우면 Amplitude 기본 주소 */
const SERVER_URL = (import.meta.env.VITE_AMPLITUDE_SERVER_URL ?? '').trim() || undefined

const DEBUG_KEY = 'annx_amp_debug'

const isAdminPath = (path = location.pathname) => path === '/admin' || path.startsWith('/admin/')

function resolveDebug(): boolean {
  if (import.meta.env.VITE_AMPLITUDE_DEBUG === 'true') return true
  try {
    const q = new URLSearchParams(location.search).get('amp_debug')
    if (q === '1') sessionStorage.setItem(DEBUG_KEY, '1')
    if (q === '0') sessionStorage.removeItem(DEBUG_KEY)
    return sessionStorage.getItem(DEBUG_KEY) === '1'
  } catch {
    return false
  }
}

let debug = false
let enabled = false
let client: BrowserClient | null = null
/** SDK 로드 전에 들어온 호출 */
const queue: ((c: BrowserClient) => void)[] = []

const log = (...args: unknown[]) => {
  if (debug) console.info('[analytics]', ...args)
}

/** undefined/빈 문자열 속성을 지워 Amplitude 화면을 깔끔하게 */
const compact = (p: Props) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== '')) as Record<
    string,
    string | number | boolean | null
  >

const run = (fn: (c: BrowserClient) => void) => {
  if (client) fn(client)
  else queue.push(fn)
}

/** 이벤트 전송. 분석이 꺼져 있으면 아무것도 하지 않는다. */
export function track(eventType: string, props: Props = {}) {
  const p = compact(props)
  log(eventType, p)
  if (!enabled) return
  run((c) => c.track(eventType, p))
}

/** 세션 유입 정보를 모든 이벤트에 `entry_*` 속성으로 붙인다 */
const entryProps = (t: Touch) =>
  compact({
    entry_channel: t.channel,
    entry_source: t.source,
    entry_medium: t.medium,
    entry_campaign: t.campaign,
    entry_ref: t.ref,
    entry_landing_page: t.landing_page,
  })

/**
 * 앱 시작 시 한 번 (main.tsx). 유입 정보는 키가 없어도 항상 계산한다 —
 * 문의 CTA 링크에 넘겨주는 값이 분석 설정과 무관하게 동작해야 하기 때문.
 */
export function initAnalytics() {
  const traffic = captureTraffic()
  debug = resolveDebug()

  if (isAdminPath()) {
    log('disabled on /admin')
    return
  }

  log('traffic', traffic)

  if (!API_KEY) {
    log('VITE_AMPLITUDE_API_KEY 가 없어 전송하지 않습니다 (dry-run).')
    return
  }
  enabled = true

  const entry = entryProps(traffic.session)

  void import('@amplitude/analytics-browser')
    .then((amplitude) => {
      const c = amplitude.createInstance()

      // 모든 이벤트(identify 제외)에 이번 세션의 유입 정보를 붙인다
      c.add({
        name: 'annx-entry-attribution',
        type: 'enrichment',
        execute: async (event: Types.Event) => {
          if (!event.event_type.startsWith('$')) {
            event.event_properties = { ...entry, ...event.event_properties }
          }
          return event
        },
      })

      c.init(API_KEY, {
        serverZone: SERVER_ZONE,
        ...(SERVER_URL ? { serverUrl: SERVER_URL } : {}),
        logLevel: debug ? amplitude.Types.LogLevel.Debug : amplitude.Types.LogLevel.Warn,
        autocapture: {
          // utm_* / referrer / 광고 클릭 ID → user property (initial_* 포함)
          attribution: {
            excludeReferrers: [/(^|\.)annx\.ai$/],
            excludeInternalReferrers: { condition: 'ifEmptyCampaign' },
          },
          // SPA 라우팅 포함. #contact 같은 해시 이동은 페이지 뷰로 치지 않는다.
          pageViews: {
            trackHistoryChanges: 'pathOnly',
            trackOn: () => !isAdminPath(),
          },
          sessions: true,
          // 아래는 PII 가 섞일 수 있거나 직접 이벤트로 대신하므로 끈다
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
          frustrationInteractions: false,
          networkTracking: false,
          webVitals: false,
        },
      })

      /**
       * SDK 가 다루지 않는 유입 정보 → user property.
       *  - traffic_*        : 이번 세션의 유입
       *  - initial_traffic_*: 최초 유입 (setOnce)
       *  - last_touch_*     : 마지막 비직접 유입 (문의 폼에 넘기는 값과 같다)
       */
      if (traffic.isNewTouch) {
        const s = traffic.session
        const f = traffic.first
        const l = traffic.last
        const id = new amplitude.Identify()
          .set('traffic_channel', s.channel)
          .set('traffic_source', s.source)
          .set('traffic_medium', s.medium)
          .set('landing_page', s.landing_page)
          .set('last_touch_channel', l.channel)
          .set('last_touch_source', l.source)
          .set('last_touch_medium', l.medium)
          .setOnce('initial_traffic_channel', f.channel)
          .setOnce('initial_traffic_source', f.source)
          .setOnce('initial_traffic_medium', f.medium)
          .setOnce('initial_landing_page', f.landing_page)
        if (s.ref) id.set('ref', s.ref)
        else id.unset('ref')
        if (l.campaign) id.set('last_touch_campaign', l.campaign)
        if (f.ref) id.setOnce('initial_ref', f.ref)
        if (f.campaign) id.setOnce('initial_traffic_campaign', f.campaign)
        c.identify(id)
      }

      client = c
      queue.splice(0).forEach((fn) => fn(c))
      log('Amplitude initialized', { serverZone: SERVER_ZONE })
    })
    .catch((e: unknown) => {
      // 광고 차단기가 SDK 청크를 막는 경우 등 — 사이트 동작에는 영향 없음
      enabled = false
      queue.length = 0
      log('Amplitude SDK load failed', e)
    })
}

/** 버튼·메뉴 링크 클릭 → `CTA Clicked` */
export function trackCta(location: string, label: string, href: string) {
  track('CTA Clicked', { cta_location: location, cta_label: label, ...describeLink(href) })
}

/* ── 링크 분류 헬퍼 (이벤트 속성용) ── */

export type LinkType = 'anchor' | 'internal' | 'external' | 'mailto' | 'tel' | 'other'

export function describeLink(href: string): {
  link_type: LinkType
  link_url: string
  link_host?: string
  target_section?: string
} {
  const raw = href.trim()
  // 메일 주소는 남기지 않는다 (회사 주소라도 이벤트에 이메일 형태 값을 두지 않는 원칙)
  if (/^mailto:/i.test(raw)) return { link_type: 'mailto', link_url: 'mailto:' }
  if (/^tel:/i.test(raw)) return { link_type: 'tel', link_url: 'tel:' }
  if (raw.startsWith('#')) {
    return { link_type: 'anchor', link_url: raw, target_section: raw.slice(1) || 'top' }
  }
  try {
    const u = new URL(raw, location.href)
    if (u.origin === location.origin) {
      return {
        link_type: u.pathname === location.pathname && u.hash ? 'anchor' : 'internal',
        link_url: u.pathname + u.search + u.hash,
        target_section: u.hash ? u.hash.slice(1) : undefined,
      }
    }
    return { link_type: 'external', link_url: u.toString(), link_host: u.hostname }
  } catch {
    return { link_type: 'other', link_url: raw.slice(0, 200) }
  }
}
